import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
  BatchPostingService, DateFormatterService, BatchPostingFileMetaDataRequestDto
} from 'remarketing-angular-library';
import { ModalComponent, SpinnerComponent, TrackDirective } from 'remarketing-ui-components-lib';
import { ErrorModalComponent } from '@ally/remarketing/shared/ui';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { take, takeUntil } from 'rxjs/operators';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import {
  NgbCalendar,
  NgbDate,
  NgbDateStruct,
  NgbInputDatepicker
} from '@ng-bootstrap/ng-bootstrap';
import { BatchPostingFileMetaDataDto, BatchPostingFileStatus } from 'remarketing-angular-library/interfaces/dtos/batch-posting.dto';
import { DatePipe, NgClass } from '@angular/common';
import {
  CsvFileUpdateWidgetComponent
} from '../csv-file-update-widget/csv-file-update-widget.component';

@Component({
    selector: 'app-csv-dashboard-widget',
    templateUrl: './csv-dashboard-widget.component.html',
    styleUrls: ['./csv-dashboard-widget.component.scss'],
    imports: [
        ReactiveFormsModule,
        NgbInputDatepicker,
        TrackDirective,
        TranslocoDirective,
        NgClass,
        CsvFileUpdateWidgetComponent,
        SpinnerComponent,
        ErrorModalComponent
    ]
})
export class CsvDashboardWidgetComponent extends SaComponent implements OnInit {
  constructor(
    private readonly translocoService: TranslocoService,
    private readonly datePipe: DatePipe,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly batchPostingService: BatchPostingService,
    private ngbCalendar: NgbCalendar,
    public dateFormatter: DateFormatterService
  ) {
    super();
  }

  get fileUploadedDateTrack() {
    return this.trackCalendars
      ? CsvDashboardWidgetComponent.calendarTrackData('File Uploaded Date')
      : { input: {}, button: {} };
  }

  @ViewChild('fromCalendar') fromCalendarButton: ElementRef;
  @ViewChild(ModalComponent) modal: ModalComponent;
  @ViewChild(ErrorModalComponent) errorModal: ErrorModalComponent;
  @Input() trackCalendars = false;
  @Output() hasDateRangeError = new EventEmitter<boolean>(false);

  file: File;
  fileName: string;
  fileType: string;
  fromDateValid = true;
  fromFormControl = new UntypedFormControl();
  fromMinDate: NgbDateStruct;
  fromMaxDate: NgbDateStruct;
  fromStartDate: NgbDateStruct;
  today: NgbDateStruct = {
    year: this.ngbCalendar.getToday().year,
    month: this.ngbCalendar.getToday().month,
    day: this.ngbCalendar.getToday().day
  };
  displayFromDate: string;

  fileList: BatchPostingFileMetaDataDto[];
  fileStatuses: BatchPostingFileStatus[];
  fileMetaDataRequest: BatchPostingFileMetaDataRequestDto;
  dashBoardForm: UntypedFormGroup;
  selectedFile: BatchPostingFileMetaDataDto;
  fileSelected = false;
  fileProcessing = false;
  resetFileStatus = false;
  appLoading = false;
  disableProcessingFile = false;

  uploadInServiceVehicleShellTranslations = {
    cantCompleteRequestError: ''
  };

  errorMessage: string;

  private static calendarTrackData(name: string) {
    return {
      input: { name: `${name}`, elem: 'calendar' },
      button: { name: `${name}`, elem: 'calendar' }
    };
  }

  ngOnInit() {
    this.getTranslations();
    this.setInitialValues();
  }

  getTranslations(): void {
    const keyParams = {
      error: {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.uploadInServiceVehicleShellTranslations.cantCompleteRequestError = translate[0].cantCompleteRequestError;
      });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.keep-span-visible')) {
      this.fileProcessing = false;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab' || event.key === 'Enter') {
      this.fileProcessing = false;
    }
  }

  checkFileStatus() {
    this.batchPostingService
      .getBatchPostingFileMetadata(this.fileMetaDataRequest)
      .pipe(take(1))
      .subscribe(files => {
        this.fileList = files;
        this.resetFileStatus = true;
      });
    if ((this.fileList && this.fileList.length === 0) || !this.fileList) {
      this.fileSelected = false;
    }
  }

  updateFromDate(fromDate: NgbDate | NgbDateStruct) {
    const fromValue = NgbDate.from(this.dateFormatter.sanitizeDateInput(fromDate));
    this.dashBoardForm.controls['uploadedFile'].setValue('default');
    if (fromDate && this.validateFromValue(fromDate)) {
      this.fromStartDate = fromValue ? fromValue : this.fromStartDate;
      this.displayFromDate = this.dateFormatter.localizeDateToString(this.fromStartDate);
      const convertDateFormat = this.datePipe.transform(new Date(this.displayFromDate), 'yyyy-MM-dd');
      this.fromDateValid = true;
      this.fileMetaDataRequest = {
        date: convertDateFormat
      };
      this.fileList = [];
      this.checkFileStatus();
    } else {
      this.fromDateValid = false;
    }
    this.hasDateRangeError.emit(!this.fromDateValid);
  }

  validateFromValue(fromDate): boolean {
    const fromDateValue = this.dateFormatter.sanitizeDateInput(fromDate);
    if (fromDateValue === undefined) return true;
    if (fromDateValue && fromDateValue.month !== null && fromDateValue.day !== null && fromDateValue.year !== null) {
      const fromDate = new NgbDate(fromDateValue.year, fromDateValue.month, fromDateValue.day);
      const isValid = this.ngbCalendar.isValid(fromDate);
      return isValid;
    } else {
      return false;
    }
  }

  focusFromCalendar() {
    this.fromCalendarButton.nativeElement.focus();
  }

  setInitialValues() {
    this.fromMinDate = { year: this.today.year - 1, month: this.today.month, day: this.today.day };
    this.fromMaxDate = this.today;
    this.dashBoardForm = this.formBuilder.group({
      uploadedFile: 'default',
      fromDate: this.datePipe.transform(new Date(), 'MM-dd-yyyy'),
      fileStatus: 'default'
    });
    if (!this.fileMetaDataRequest) {
      const currentDate = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
      this.fileMetaDataRequest = { date: currentDate };
    }
    this.batchPostingService
      .getBatchPostingFileStatus()
      .pipe(take(1))
      .subscribe(statuses => {
        this.fileStatuses = statuses;
      });
    this.displayFromDate = this.datePipe.transform(new Date(), 'MM-dd-yyyy');
    this.checkFileStatus();
  }

  setSelectedFile() {
    this.selectedFile = this.fileList.filter(file => file.fileName === this.dashBoardForm.get('uploadedFile').value)[0];
    if (this.selectedFile) {
      this.fileSelected = true;
      this.resetFileStatus = false;
      if (this.selectedFile.status === BatchPostingFileStatus.UNPROCESSABLE) {
        this.disableProcessingFile = true;
      } else {
        this.disableProcessingFile = false;
      }
    } else {
      this.fileSelected = false;
    }
  }

  startFileProcessing() {
    this.appLoading = true;
    this.batchPostingService.startProcessingBatchFile(this.selectedFile.fileUUid).subscribe({
      next: () => {
        this.fileProcessing = true;
        this.appLoading = false;
      },
      error: error => {
        console.error(error);
        // when service is down or timing out waiting for response, to show generic error.
        this.errorMessage = this.uploadInServiceVehicleShellTranslations.cantCompleteRequestError;
        this.errorModal.message = this.errorMessage;
        this.errorModal.show();
        this.fileProcessing = false;
      }
    });
  }

  resetFileNote() {
    this.resetFileStatus = true;
  }
}
